import { type Exact, exactPi } from "@/lib/tool-kit/geometry/exact";
import {
  addR,
  divR,
  intR,
  mulR,
  parseRational,
  type Rational,
  signR,
  subR,
} from "@/lib/tool-kit/math/rational";

/**
 * Polygon angle facts, exact.
 *
 * The interior angles of any simple n-gon add up to (n − 2) × 180°. In a regular polygon
 * they are equal, so each is (n − 2) × 180° / n, each exterior angle is 360° / n, and
 * the central angle (from the center to two neighbouring corners) is 360° / n as well.
 * Everything is a rational number of degrees, so 7 sides gives 900/7° — never a rounded
 * 128.57° that cannot be turned back into n.
 */

export const MIN_SIDES = 3;
export const MAX_SIDES = 1000;

const STRAIGHT = intR(180);
const FULL_TURN = intR(360);

export interface PolygonFacts {
  sides: number;
  interiorSum: Rational;
  interiorEach: Rational;
  exteriorEach: Rational;
  exteriorSum: Rational;
  central: Rational;
  diagonals: number;
}

export function validSides(sides: number): boolean {
  return Number.isInteger(sides) && sides >= MIN_SIDES && sides <= MAX_SIDES;
}

/** The angle facts of a polygon with `sides` sides (3 to 1000). */
export function polygonFacts(sides: number): PolygonFacts {
  const n = intR(sides);
  const interiorSum = mulR(intR(sides - 2), STRAIGHT);
  return {
    sides,
    interiorSum,
    interiorEach: divR(interiorSum, n),
    exteriorEach: divR(FULL_TURN, n),
    exteriorSum: FULL_TURN,
    central: divR(FULL_TURN, n),
    diagonals: (sides * (sides - 3)) / 2,
  };
}

/** An angle in degrees as a rational multiple of π radians, written (n−2)π/n and so on. */
export function radiansExact(degrees: Rational): Exact {
  return exactPi(divR(degrees, STRAIGHT));
}

export type AngleKind = "interior" | "exterior";

export type SidesFromAngle =
  | { ok: true; sides: Rational; whole: boolean }
  | { ok: false; error: "range" };

const MAX_EXTERIOR = intR(120);

/**
 * The number of sides a regular polygon would need for an interior or exterior angle.
 * 360 / (180 − interior) or 360 / exterior: a whole number of at least 3 means the
 * polygon exists; anything else says no regular polygon has that angle.
 */
export function sidesFromAngle(
  kind: AngleKind,
  degrees: Rational
): SidesFromAngle {
  if (signR(degrees) <= 0) {
    return { ok: false, error: "range" };
  }
  let exterior: Rational;
  if (kind === "interior") {
    if (signR(subR(degrees, STRAIGHT)) >= 0) {
      return { ok: false, error: "range" };
    }
    exterior = subR(STRAIGHT, degrees);
  } else {
    exterior = degrees;
  }
  if (signR(subR(exterior, MAX_EXTERIOR)) > 0) {
    return { ok: false, error: "range" };
  }
  const sides = divR(FULL_TURN, exterior);
  return {
    ok: true,
    sides,
    whole: sides.den === BigInt(1) && sides.num >= BigInt(MIN_SIDES),
  };
}

export type MissingAngleError = "count" | "range" | "total";

export type MissingAngle =
  | {
      ok: true;
      total: Rational;
      knownSum: Rational;
      missing: Rational;
      /** At least 180°: the polygon would be concave at that corner. */
      reflex: boolean;
    }
  | { ok: false; error: MissingAngleError };

const SEPARATORS = /[\s;]+/;
const TRAILING_COMMA = /,+$/;

/** Angles separated by spaces, semicolons or new lines (a trailing comma is ignored). */
export function parseAngleList(text: string): Rational[] | null {
  const tokens = text
    .split(SEPARATORS)
    .map((token) => token.replace(TRAILING_COMMA, ""))
    .filter((token) => token !== "");
  const values = tokens.map((token) => parseRational(token));
  if (values.some((value) => value === null)) {
    return null;
  }
  return values as Rational[];
}

/** The one interior angle that completes a polygon, given the other n − 1. */
export function missingAngle(sides: number, known: Rational[]): MissingAngle {
  if (known.length !== sides - 1) {
    return { ok: false, error: "count" };
  }
  if (
    known.some(
      (angle) => signR(angle) <= 0 || signR(subR(angle, FULL_TURN)) >= 0
    )
  ) {
    return { ok: false, error: "range" };
  }
  const total = mulR(intR(sides - 2), STRAIGHT);
  const knownSum = known.reduce(addR, intR(0));
  const missing = subR(total, knownSum);
  if (signR(missing) <= 0 || signR(subR(missing, FULL_TURN)) >= 0) {
    return { ok: false, error: "total" };
  }
  return {
    ok: true,
    total,
    knownSum,
    missing,
    reflex: signR(subR(missing, STRAIGHT)) >= 0,
  };
}
